import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import classnames from 'classnames';
import {
  DragSource,
  DropTarget,
} from 'react-dnd';
import { getEmptyImage } from 'react-dnd-html5-backend';
import { Input } from 'reactstrap';
import { injectIntl } from 'react-intl';

import {
  getCollection,
  addSelectedCollection,
  removeSelectedCollection,
} from '../../actions/Collections';

import { isArchive, isTrash } from '../../helpers/App';
import { thumbnailPlaceholder } from '../../helpers/Collections';
import {
  collectionsTarget,
  collectionsSource,
} from '../../helpers/DNDCollections';
import ItemTypes from '../../helpers/DNDItemTypes';

import CollectionsActions from './Actions';
import CollectionsAccess from './Access';
import CollectionsDate from './Date';
import DropItemOverlay from '../DropItemOverlay';
import CollectionDescriptionIcon from './DescriptionIcon';
import CollectionSyncedIcon from './SyncedIcon';
import TrashRemainingDays from '../TrashRemainingDays';

class CollectionsListItem extends PureComponent {
  componentDidMount() {
    const { connectDragPreview } = this.props;

    if (connectDragPreview) {
      connectDragPreview(getEmptyImage(), {
        captureDraggingState: true,
      });
    }
  }

  handleCollectionClick(collection) {
    const {
      collectionGroup,
      getCollectionAction,
    } = this.props;

    if (collectionGroup) {
      getCollectionAction({
        group: collectionGroup,
        ...collection,
      });
    }
  }

  render() {
    const {
      intl,
      collection,
      isSelected,
      selectCollection,
      unselectCollection,
      connectDragSource,
      connectDropTarget,
      isDragging,
      isMoved,
      isOver,
      canDrop,
      reorderingMode,
    } = this.props;
    const featuredFile = collection.featured_file;

    const getLinkTo = () => {
      if (isArchive()) {
        return `/archive/collections/${collection.uuid}`;
      }
      if (isTrash()) {
        return `/trash/collections/${collection.uuid}`;
      }
      return `/collections/${collection.uuid}`;
    };

    return connectDragSource(connectDropTarget((
      <tr
        id={collection.uuid}
        className={classnames(
          'list-item',
          {
            dragging: isDragging,
            hovered: isOver,
          },
        )}
      >
        <td className="selector">
          <DropItemOverlay
            isOver={isOver}
            canDrop={canDrop}
            reordering={reorderingMode}
          />
          <Input
            type="checkbox"
            className="input-check check-s form-check-input"
            id={`collection-${collection.uuid}`}
            checked={isSelected}
            onChange={() => {
              if (isSelected) {
                unselectCollection(collection.uuid);
              } else {
                selectCollection(collection);
              }
            }}
          />
        </td>
        <td className="collection-image">
          <div>
            {isMoved && (
              <span className="list-item-moved-icon">
                <i className="fas fa-sync fa-spin" />
              </span>
            )}
            <Link
              to={`${isArchive() ? '/archive' : ''}/collections/${collection.uuid}`}
              className="list-item-img-wrapper"
              onClick={() => { this.handleCollectionClick(collection); }}
            >
              {featuredFile ? (
                <img
                  className="list-item-img"
                  width="100%"
                  src={featuredFile.urls.thumbnail}
                  srcSet={`${featuredFile.urls.thumbnail} 1x, ${featuredFile.urls.thumbnailx2} 2x`}
                  alt={collection.name}
                />
              ) : (
                <img
                  className="list-item-img"
                  width="100%"
                  src={thumbnailPlaceholder}
                  alt={collection.name}
                />
              )}
            </Link>
          </div>
        </td>
        <td className="collection-name">
          <h5 className="list-item-title">
            <Link
              to={getLinkTo()}
              onClick={() => { this.handleCollectionClick(collection); }}
            >
              {collection.smart && <span className="text-glow-violet mr-2"><i className="fas fa-bolt" /></span>}
              {collection.name}
            </Link>
          </h5>
          <div className="list-item-details collection-details">
            <div>
              <CollectionsAccess collection={collection} />
              {collection.files_count > 0 && (
                <span className="collection-files-count">
                  { intl.formatMessage({
                    defaultMessage: '{count} {count, plural, one {file} other {files}}',
                  }, { count: collection.files_count }) }
                </span>
              )}
              {(collection.child_collections_count && collection.child_collections_count > 0) ? (
                <span className="collection-files-count">
                  { intl.formatMessage({
                    defaultMessage: '{count} {count, plural, one {collection} other {collections}}',
                  }, { count: collection.child_collections_count }) }
                </span>
              ) : ''}
              <CollectionSyncedIcon collection={collection} />
              <CollectionDescriptionIcon
                collection={collection}
              />
              <TrashRemainingDays
                collection={collection}
              />
            </div>
          </div>
        </td>
        <td className="collection-owner">
          {collection.user ? collection.user.name : intl.formatMessage({ defaultMessage: '[removed user]' })}
        </td>
        <td className="collection-created">
          <CollectionsDate
            collection={collection}
          />
        </td>
        <td className="action-more">
          <CollectionsActions
            collection={collection}
            collectionsView="list"
          />
        </td>
      </tr>
    )));
  }
}

const mapStateToProps = state => ({
  selectedFiles: state.Files.selectedFiles,
});

const mapDispatchToProps = dispatch => ({
  getCollectionAction: (collection) => {
    dispatch(getCollection(collection));
  },
  selectCollection: (collection) => {
    dispatch(addSelectedCollection(collection));
  },
  unselectCollection: (collectionUuid) => {
    dispatch(removeSelectedCollection(collectionUuid));
  },
});

CollectionsListItem.propTypes = {
  intl: PropTypes.object.isRequired,
  collectionGroup: PropTypes.object,
  collection: PropTypes.object.isRequired,
  getCollectionAction: PropTypes.func.isRequired,
  selectCollection: PropTypes.func.isRequired,
  unselectCollection: PropTypes.func.isRequired,
  isSelected: PropTypes.bool.isRequired,
  connectDragSource: PropTypes.func.isRequired,
  connectDropTarget: PropTypes.func.isRequired,
  connectDragPreview: PropTypes.func.isRequired,
  isDragging: PropTypes.bool.isRequired,
  isMoved: PropTypes.bool.isRequired,
  isOver: PropTypes.bool.isRequired,
  canDrop: PropTypes.bool.isRequired,
  reorderingMode: PropTypes.bool.isRequired,
};

CollectionsListItem.defaultProps = {
  collectionGroup: null,
};

export default
injectIntl(connect(mapStateToProps, mapDispatchToProps)(
  DragSource(ItemTypes.COLLECTION, collectionsSource, (DNDconnect, monitor) => ({
    connectDragSource: DNDconnect.dragSource(),
    connectDragPreview: DNDconnect.dragPreview(),
    isDragging: monitor.isDragging(),
  }))(DropTarget([
    ItemTypes.COLLECTION,
    ItemTypes.FILE,
  ], collectionsTarget, (DNDconnect, monitor) => ({
    connectDropTarget: DNDconnect.dropTarget(),
    isOver: monitor.isOver(),
    canDrop: monitor.canDrop(),
  }))(CollectionsListItem)),
));
